// Page de gestion des Locataires avec design amélioré
// Fichier: /var/www/squarecom/frontend/src/pages/Tenants.js

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
  Pagination,
  CircularProgress,
  Avatar,
  InputAdornment,
  TableContainer,
  Container,
  useTheme,
  alpha
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Apartment as ApartmentIcon,
  Person as PersonIcon,
  LocationCity as LocationCityIcon
} from '@mui/icons-material';

// Utilitaire : appel fetch avec Authorization: Bearer <token>
async function fetchWithAuth(url, options = {}) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { ...options, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) return res.json();
  return res.text();
}

function toCSV(rows) {
  const headers = [
    'firstName',
    'lastName',
    'email',
    'phone',
    'apartmentNumber',
    'residenceName',
  ];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes(';') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [headers.join(';')];
  for (const r of rows) {
    lines.push(
      [
        r.firstName,
        r.lastName,
        r.email,
        r.phone,
        r.apartmentNumber,
        r.residenceName,
      ]
        .map(escape)
        .join(';')
    );
  }
  return lines.join('\n');
}

// Fonction pour générer une couleur basée sur le nom
function stringToColor(string) {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

// Fonction pour obtenir les initiales
function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

export default function Tenants() {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [residences, setResidences] = useState([]);
  const [selectedResidence, setSelectedResidence] = useState('all');
  const [tenants, setTenants] = useState([]);
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });

  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await fetchWithAuth('/api/residences');
        if (!mounted) return;
        setResidences(data.residences || []);
      } catch (e) {
        console.error(e);
        setSnack({ open: true, message: `Erreur chargement résidences: ${e.message}`, severity: 'error' });
      }
    })();
    return () => { mounted = false; };
  }, []);

  const loadTenants = async (rid) => {
    setLoading(true);
    try {
      let list = [];
      if (!rid || rid === 'all') {
        try {
          const dataAll = await fetchWithAuth('/api/tenants');
          list = dataAll.tenants || [];
        } catch (_) {
          const chunks = await Promise.all(
            residences.map((r) => fetchWithAuth(`/api/tenants?residenceId=${r.id}`))
          );
          list = chunks.flatMap((c) => c.tenants || []);
        }
      } else {
        const data = await fetchWithAuth(`/api/tenants?residenceId=${rid}`);
        list = data.tenants || [];
      }
      const mapRes = Object.fromEntries(residences.map((r) => [r.id, r.name]));
      const withNames = list.map((t) => ({ ...t, residenceName: mapRes[t.residenceId] || '' }));
      setTenants(withNames);
    } catch (e) {
      console.error(e);
      setSnack({ open: true, message: `Erreur chargement locataires: ${e.message}`, severity: 'error' });
      setTenants([]);
    } finally {
      setLoading(false);
      setPage(1);
    }
  };

  useEffect(() => {
    if (!residences.length) return;
    loadTenants(selectedResidence);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [residences, selectedResidence]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => {
      const hay = `${t.firstName} ${t.lastName} ${t.email} ${t.phone} ${t.apartmentNumber} ${t.residenceName}`.toLowerCase();
      return hay.includes(q);
    });
  }, [search, tenants]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pageRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleExport = () => {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'locataires.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCopyEmails = async () => {
    try {
      const emails = filtered.map((t) => t.email).filter(Boolean).join(', ');
      await navigator.clipboard.writeText(emails);
      setSnack({ open: true, message: 'Adresses e‑mail copiées dans le presse‑papiers', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: `Impossible de copier: ${e.message}`, severity: 'error' });
    }
  };

  return (
    <Container maxWidth="xl">
      <Box py={3}>
        {/* Header */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={4}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Typography variant="h4" fontWeight={600}>
              Locataires
            </Typography>
            <Chip 
              label={`${filtered.length} résultat${filtered.length > 1 ? 's' : ''}`}
              color="primary"
              variant="outlined"
            />
          </Stack>
          
          <Stack direction="row" spacing={1}>
            <Tooltip title="Rafraîchir">
              <IconButton 
                onClick={() => loadTenants(selectedResidence)}
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copier les emails">
              <IconButton 
                onClick={handleCopyEmails}
                sx={{ 
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ borderRadius: 2 }}
            >
              EXPORTER CSV
            </Button>
          </Stack>
        </Stack>

        {/* Filters */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 3, 
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.8)
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl sx={{ minWidth: 250 }}>
              <InputLabel>Résidence</InputLabel>
              <Select
                value={selectedResidence}
                onChange={(e) => setSelectedResidence(e.target.value)}
                label="Résidence"
                startAdornment={
                  <InputAdornment position="start">
                    <LocationCityIcon sx={{ color: 'action.active', ml: 1 }} />
                  </InputAdornment>
                }
              >
                <MenuItem value="all">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>Toutes les résidences</Typography>
                  </Stack>
                </MenuItem>
                {residences.map((r) => (
                  <MenuItem key={r.id} value={r.id}>
                    {r.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              placeholder="Rechercher (nom, email, téléphone, appart, résidence)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
        </Paper>

        {/* Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer 
            component={Paper} 
            elevation={0}
            sx={{ 
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              overflow: 'hidden'
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                  <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PersonIcon fontSize="small" />
                      <Typography>Locataire</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EmailIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography>Email</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <PhoneIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography>Téléphone</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <HomeIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography>Appartement</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ApartmentIcon fontSize="small" sx={{ color: 'action.active' }} />
                      <Typography>Résidence</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableHead>
              
              <TableBody>
                {pageRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">
                        Aucun locataire trouvé
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  pageRows.map((tenant, index) => (
                    <TableRow 
                      key={tenant.id || index}
                      hover
                      sx={{
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.02)
                        }
                      }}
                    >
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Avatar 
                            sx={{ 
                              bgcolor: stringToColor(`${tenant.firstName} ${tenant.lastName}`),
                              width: 40,
                              height: 40,
                              fontSize: '0.9rem'
                            }}
                          >
                            {getInitials(tenant.firstName, tenant.lastName)}
                          </Avatar>
                          <Box>
                            <Typography fontWeight={500}>
                              {tenant.firstName} {tenant.lastName}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      
                      <TableCell>
                        <Typography 
                          component="a" 
                          href={`mailto:${tenant.email}`}
                          sx={{ 
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {tenant.email}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Typography 
                          component="a" 
                          href={`tel:${tenant.phone}`}
                          sx={{ 
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          {tenant.phone || '—'}
                        </Typography>
                      </TableCell>
                      
                      <TableCell>
                        <Chip 
                          label={tenant.apartmentNumber || '—'}
                          size="small"
                          sx={{ 
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: theme.palette.info.dark,
                            fontWeight: 500
                          }}
                        />
                      </TableCell>
                      
                      <TableCell>
                        <Typography variant="body2">
                          {tenant.residenceName || '—'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, p) => setPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}

        {/* Snackbar */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={() => setSnack({ ...snack, open: false })} 
            severity={snack.severity}
            variant="filled"
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
